
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import Nodata from '@/components/page/nodata.vue'
import { getStockClass, jumpToStockChart } from '@/utils/stock'
import strategyApi from '@/api/strategy'
import ws from '@/websocket/index'
import WXQuotationWebSocket from '@/websocket/quotationWebsocket'
import { zh_tran, objTran } from '@/utils/lang.js'

export interface PositionItem {
  symbol: string
  name: string
  market: string
  isDelay: boolean
  changeRate: number
  price: number
  addTime: string | number
}

export default defineComponent({
  name: 'PositionPanel',
  components: { Nodata },
  props: {
    code: { type: String, default: '' },
    isTraditional: { type: Boolean, default: false }
  },
  setup(props) {
    const route = useRoute()

    const list       = ref<PositionItem[]>([])
    const loading    = ref(false)
    const updateInfo = ref('')

    // token / 主题 从路由参数读取
    const token = (route.query.token as string) || ''
    const isTraditionalFromRoute = (route.query['Accept-Language'] as string) === 'zh-Hant'
    const isTraditionalVal = computed(() => props.isTraditional || isTraditionalFromRoute)
    const isDark = computed(() => route.query.theme !== 'light')

    // PositionPanel 局部主题变量
    const panelVars = computed(() => isDark.value ? ({
      panelBg:      '#111424',
      gapBg:        '#000000',
      panelBorder:  'rgba(255,255,255,0.06)',
      headerBg:     '#1A1D2F',
      headerText:   '#ffffff',
      thHeaderColor:'#7C7E87',
      textPrimary:  'rgba(255,255,255,0.85)',
      textSecondary:'rgba(255,255,255,0.45)',
      rowHover:     'rgba(255,255,255,0.04)',
      btnBg:        '#4a9eff',
      btnText:      '#fff',
      spinBorder:   'rgba(255,255,255,0.15)',
      spinTop:      'rgba(255,255,255,0.6)',
    }) : ({
      panelBg:      '#FFFFFF',
      gapBg:        '#E6E9EF',
      panelBorder:  '#e8e8e8',
      headerBg:     '#F0F1F7',
      headerText:   '#1d1d1d',
      thHeaderColor:'#808080',
      textPrimary:  '#1d1d1d',
      textSecondary:'#7d7d7d',
      rowHover:     '#f5f7fa',
      btnBg:        '#4a9eff',
      btnText:      '#fff',
      spinBorder:   'rgba(0,0,0,0.1)',
      spinTop:      'rgba(0,0,0,0.45)',
    }))

    // 延时行情标识
    const hkDelay = route.query.HKDELAY === '1'
    const usDelay = route.query.USDELAY === '1'

    // 涨跌偏好
    const prefer = route.query.preferGreenUptrendRedDowntrend as string | undefined

    // 文字转换：繁体时调用 .tran()，简体直接返回
    function tran(text: string) {
      if (isTraditionalVal.value) {
        return (text as any).tran()
      }
      return text
    }

    // 数据/DOM 变化后重新触发 DOM 转换
    function switchLanguage() {
      if (isTraditionalVal.value) {
        nextTick(() => zh_tran('t'))
      }
    }

    // ── WS 行情订阅 ──────────────────────────────────────────
    const WS_LIST_KEY = 'positionPanelPositions'
    const WS_EVENT = WXQuotationWebSocket.EVENT
    /** 当前待订阅列表，切换策略 / WS 重连时始终取最新 */
    const pendingPositions = ref<PositionItem[]>([])

    const US_MARKETS = ['NASDAQ', 'NYSE', 'AMEX', 'US']

    const normalizeQuoteMarket = (market: string) => {
      const m = (market || '').toUpperCase().replace(/^DELAY_/, '')
      if (m === 'HK') return 'HKSE'
      if (m === 'NA') return 'NASDAQ'
      return m
    }

    const isSameQuoteMarket = (a: string, b: string) => {
      const ma = normalizeQuoteMarket(a)
      const mb = normalizeQuoteMarket(b)
      if (ma === mb) return true
      return US_MARKETS.includes(ma) && US_MARKETS.includes(mb)
    }

    /** 接口 market → WS 订阅 market（含延时） */
    const toWsMarket = (market: string) => {
      let m = (market || '').toUpperCase()
      const isHK = m === 'HKSE' || m === 'HK'
      const isUS = US_MARKETS.includes(m)
      if (isHK && hkDelay) return 'DELAY_HKSE'
      if (isUS && usDelay) return `DELAY_${m}`
      return m
    }

    const buildStockList = (positions: PositionItem[]) =>
      positions.map(p => ({ market: toWsMarket(p.market), symbol: p.symbol }))

    const parseDynaList = (data: any): any[] => {
      const raw = data?.QuoteData?.DynaData
      if (!raw) return []
      return Array.isArray(raw) ? raw : [raw]
    }

    const findPositionByQuote = (code: string, market: string) =>
      list.value.find(p => {
        if (String(p.symbol) !== String(code)) return false
        return !market || isSameQuoteMarket(p.market, market)
      })

    const applyQuoteUpdate = (d: any) => {
      const code = d.InstrumentID || d.Inst
      if (!code) return
      const target = findPositionByQuote(code, d.ExchangeID || d.Market || '')
      if (!target) return
      if (d.LastPrice != null) target.price = d.LastPrice
      if (d.UpdownRate != null) target.changeRate = d.UpdownRate
      else if (d.Updown != null) target.changeRate = d.Updown
    }

    const onHqListUpdate = (data: any) => {
      if (!list.value.length) return
      parseDynaList(data).forEach(applyQuoteUpdate)
    }

    const unsubscribePositions = () => {
      ws.websocket?.safeUnsubscribeList(WS_LIST_KEY)
    }

    const syncSubscribe = () => {
      if (!ws.websocket || !pendingPositions.value.length) return
      ws.websocket.safeSubscribeList(WS_LIST_KEY, buildStockList(pendingPositions.value))
    }

    const subscribePositions = (positions: PositionItem[]) => {
      pendingPositions.value = positions
      unsubscribePositions()
      if (!positions.length) return
      syncSubscribe()
    }

    // ── 接口拉取 ─────────────────────────────────────────────
    const fetchStockTrack = async (code: string) => {
      if (!code || !token) return
      loading.value = true
      list.value = []
      updateInfo.value = ''
      unsubscribePositions()
      pendingPositions.value = []
      try {
        const res: any = await strategyApi.getStockTrack({ token, code })
        if (res?.data) {
          const { stocks } = res.data
          updateInfo.value = `${(stocks || []).length}${tran('只')}`
          if (stocks?.length) {
            const positions: PositionItem[] = stocks.map((s: any) => ({
              symbol:     s.symbol,
              name:       s.name,
              market:     s.market,
              isDelay:    false,
              changeRate: s.pxChangeRate ?? 0,
              price:      s.lastPx ?? 0,
              addTime:    s.buyTime || ''
            }))
            list.value = isTraditionalVal.value
              ? objTran('t', positions) as PositionItem[]
              : positions
            subscribePositions(list.value)
          } else {
            subscribePositions([])
          }
        }
      } catch (e) {
        console.error('[PositionPanel] 获取持仓数据失败', e)
      } finally {
        loading.value = false
        switchLanguage()
      }
    }

    const clearPositions = () => {
      list.value = []
      updateInfo.value = ''
      subscribePositions([])
      loading.value = false
    }

    watch(() => props.code, (val) => {
      if (val) {
        fetchStockTrack(val)
      } else {
        clearPositions()
      }
    }, { immediate: true })

    onMounted(() => {
      switchLanguage()
      ws.websocket?.on(WS_EVENT.onHQLIST, onHqListUpdate)
      ws.websocket?.on(WS_EVENT.onOpen, syncSubscribe)
    })

    onBeforeUnmount(() => {
      ws.websocket?.removeListener(WS_EVENT.onHQLIST, onHqListUpdate)
      ws.websocket?.removeListener(WS_EVENT.onOpen, syncSubscribe)
      unsubscribePositions()
      pendingPositions.value = []
    })

    const isHKMarket = (market: string) => {
      const m = (market || '').toLowerCase()
      return m === 'hkse' || m === 'hk' || m.includes('hk')
    }

    const getMarketLabel = (market: string) => {
      const m = (market || '').toLowerCase()
      // if (m === 'hkse' || m === 'hk' || m.includes('hk')) return 'HK'
      // if (m === 'nyse' || m === 'nasdaq' || m === 'us' || m.includes('us')) return 'US'
      // return market.toUpperCase()
      return isHKMarket(m)
  ? 'HK'
  : 'US'
    }

    const isDelayed = (market: string) => {
      const m = (market || '').toLowerCase()
      const isHK = m === 'hkse' || m === 'hk' || m.includes('hk')
      const isUS = m === 'nyse' || m === 'nasdaq' || m === 'us' || m.includes('us')
      if (isHK && hkDelay) return true
      if (isUS && usDelay) return true
      return false
    }

    const getChangeClass = (val: number | undefined) => {
      return getStockClass(val ?? null, prefer)
    }

    const formatPct = (val: number | undefined) => {
      if (val == null) return '--'
      const pct  = val * 100
      const sign = pct > 0 ? '+' : ''
      return sign + pct.toFixed(2) + '%'
    }

    const formatPrice = (val: number | undefined) => {
      if (val == null) return '--'
      return val.toFixed(3)
    }

    const formatDate = (val: string | number | undefined) => {
      if (!val) return '--'
      const ts = typeof val === 'string' ? Number(val) : val
      if (!ts || isNaN(ts)) return String(val)
      const d   = new Date(ts)
      const y   = d.getFullYear()
      const m   = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }

    const goStockDetail = (item: PositionItem) => {
      const market = (item.market || '').toUpperCase()
      jumpToStockChart(`${item.symbol}.${market}`)
    }

    const handleTrade = (item: PositionItem) => {
      const market = (item.market || '').toUpperCase()
      const url = `gw://funccall?funcname=OnGoJiaoYi;market=${market};symbol=${item.symbol};type=1;name=${item.name};exchange=${market};future_type=;derivative_type=;is_stock_index=;ei=;`
      window.open(url)
    }

    return {
      list,
      loading,
      isDark,
      panelVars,
      updateInfo,
      tran,
      isHKMarket,
      getMarketLabel,
      isDelayed,
      getChangeClass,
      formatPct,
      formatPrice,
      formatDate,
      goStockDetail,
      handleTrade
    }
  }
})
